import { Container, Typography, Box, Link, Card, CardContent } from '@mui/material';
import Grid from '@mui/material/Grid2';

function LyricJam() {
    return (
        <Container sx={{ padding: '40px 0' }}>
            <Typography variant="h2" gutterBottom sx={{ textAlign: 'left', marginBottom: '24px' }}>
                LyricJam: A System for Generating Lyrics for Live Instrumental Music
            </Typography>

            <Typography variant="body1" sx={{ fontSize: '1.1rem', marginBottom: '30px', fontStyle: 'italic' }}>
                Published at the 12th International Conference on Computational Creativity (ICCC 2021)
            </Typography>

            {/* Interactive Demo Section */}
            <Box sx={{ marginBottom: '40px' }}>
                <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
                    Try LyricJam
                </Typography>
                <Typography variant="body1" sx={{ marginBottom: '20px' }}>
                    Experience LyricJam directly through our web application:
                </Typography>
                <Card sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box
                        component="img"
                        src="/images/LyricJamMode.jpg"
                        alt="LyricJam Interface"
                        sx={{
                            width: '100%',
                            height: '300px',
                            objectFit: 'cover',
                        }}
                    />
                    <Box sx={{
                        padding: '30px',
                        textAlign: 'center'
                    }}>
                        <Typography variant="h5" sx={{ marginBottom: '15px', fontWeight: 'bold' }}>
                            Live Web Application
                        </Typography>
                        <Typography variant="body1" sx={{ marginBottom: '20px' }}>
                            Generate lyrics in real-time during live instrumental music performances
                        </Typography>
                        <Link
                            href="https://lyricjam.ai"
                            target="_blank"
                            rel="noopener"
                            className="button-style"
                            sx={{
                                fontSize: '1.1rem'
                            }}
                        >
                            Launch LyricJam
                        </Link>
                    </Box>
                </Card>
            </Box>

            {/* Abstract Section */}
            <Box sx={{ marginBottom: '40px' }}>
                <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
                    Abstract
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6, textAlign: 'justify' }}>
                    We describe a real-time system that receives a live audio stream from a jam session and generates lyric lines that
                    are congruent with the live music being played. Two novel approaches are proposed to align the learned latent spaces
                    of audio and text representations that allow the system to generate novel lyric lines matching live instrumental music.
                    One approach is based on adversarial alignment of latent representations of audio and lyrics, while the other approach
                    learns to transfer the topology from the music latent space to the lyric latent space. A user study with music artists
                    using the system showed that the system was useful not only in lyric composition, but also encouraged the artists to
                    improvise and find new musical expressions. Another user study demonstrated that users preferred the lines generated
                    using the proposed methods to the lines generated by a baseline model.
                </Typography>
            </Box>

            {/* Key Innovation Section */}
            <Box sx={{ marginBottom: '40px' }}>
                <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
                    Key Innovation
                </Typography>
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                                    Real-Time Lyric Generation
                                </Typography>
                                <Typography variant="body2">
                                    First system to receive live audio streams from jam sessions and generate lyrics that are congruent
                                    with the live music being played, creating a feedback loop that encourages musical exploration.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                                    Novel Alignment Methods
                                </Typography>
                                <Typography variant="body2">
                                    Two innovative approaches to align audio and text latent spaces: GAN-based adversarial alignment
                                    and topology transfer from music to lyric latent space using variational autoencoders.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                                    Creative Feedback Loop
                                </Typography>
                                <Typography variant="body2">
                                    Artists can see generated lines in real-time, creating a feedback loop where lyrics suggest themes
                                    and phrases that guide musical expressions and instrumentation as they play.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                                    Encourages Improvisation
                                </Typography>
                                <Typography variant="body2">
                                    User studies revealed the system encourages artists to try new sounds, experiment with musical
                                    expressions, and acts as an uncritical jam partner that always responds to their playing.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            {/* Conference Presentation Video Section */}
            <Box sx={{ marginBottom: '40px' }}>
                <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
                    Conference Presentation
                </Typography>
                <Typography variant="body1" sx={{ marginBottom: '20px' }}>
                    Watch the full presentation from ICCC 2021:
                </Typography>
                <Box sx={{
                    position: 'relative',
                    paddingBottom: '56.25%', /* 16:9 aspect ratio */
                    height: 0,
                    overflow: 'hidden',
                    maxWidth: '100%',
                    backgroundColor: '#000'
                }}>
                    <iframe
                        src="https://player.vimeo.com/video/589495189?title=0&amp;byline=0&amp;portrait=0&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479"
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            border: 'none'
                        }}
                        allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                        allowFullScreen
                        title="LyricJam presentation at ICCC'21"
                    />
                </Box>
            </Box>

            {/* Technical Approach Section */}
            <Box sx={{ marginBottom: '40px' }}>
                <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
                    Technical Approach
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6, marginBottom: '20px' }}>
                    LyricJam employs two main neural network architectures working together:
                </Typography>

                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Card sx={{ height: '100%', backgroundColor: '#f8f9fa' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#d32f2f' }}>
                                    Approach 1: GAN-based Alignment
                                </Typography>
                                <Typography variant="body2" sx={{ marginBottom: '10px' }}>
                                    <strong>Spectrogram VAE:</strong> Learns latent representations of Mel-spectrograms from audio clips
                                </Typography>
                                <Typography variant="body2" sx={{ marginBottom: '10px' }}>
                                    <strong>Text-CVAE:</strong> Conditional VAE for learning lyric line representations
                                </Typography>
                                <Typography variant="body2">
                                    <strong>GAN:</strong> Adversarially aligns the latent spaces by learning to predict lyric embeddings from audio embeddings
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Card sx={{ height: '100%', backgroundColor: '#f8f9fa' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                                    Approach 2: Topology Transfer
                                </Typography>
                                <Typography variant="body2" sx={{ marginBottom: '10px' }}>
                                    <strong>Shared Latent Space:</strong> Uses spec-VAE posteriors as priors in text-CVAE training
                                </Typography>
                                <Typography variant="body2" sx={{ marginBottom: '10px' }}>
                                    <strong>Topology Preservation:</strong> Similar sounding audio clips have corresponding lyrics in neighboring regions
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Direct Mapping:</strong> Enables direct sampling from audio-conditioned lyric distributions
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            {/* Results Section */}
            <Box sx={{ marginBottom: '40px' }}>
                <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
                    User Study Results
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6, marginBottom: '20px' }}>
                    Our evaluation with musicians and general users demonstrated the effectiveness of both proposed approaches:
                </Typography>

                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <Card sx={{ height: '100%', textAlign: 'center' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#388e3c' }}>
                                    User Preference Study
                                </Typography>
                                <Typography variant="body2" sx={{ marginBottom: '10px' }}>
                                    Users significantly preferred lines generated by both proposed methods over baseline CVAE
                                    across different instrumental songs and moods
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <Card sx={{ height: '100%', textAlign: 'center' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                                    Creative Benefits
                                </Typography>
                                <Typography variant="body2" sx={{ marginBottom: '10px' }}>
                                    Musicians reported the system encouraged improvisation, experimentation with new sounds,
                                    and acted as an uncritical jam partner
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <Card sx={{ height: '100%', textAlign: 'center' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#f57c00' }}>
                                    Multiple Use Cases
                                </Typography>
                                <Typography variant="body2" sx={{ marginBottom: '10px' }}>
                                    Beyond lyric composition, users found the system useful for general music playing,
                                    creative inspiration, and overcoming writer's block
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            {/* App Usage and Impact Section */}
            <Box sx={{ marginBottom: '40px' }}>
                <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
                    Real-World Impact and Usage
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6, marginBottom: '20px' }}>
                    Since its public release, LyricJam has demonstrated significant real-world adoption and impact:
                </Typography>

                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Card sx={{ height: '100%', backgroundColor: '#f8f9fa' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                                    User Adoption
                                </Typography>
                                <Typography variant="body2" sx={{ marginBottom: '10px' }}>
                                    <strong>30,000+ unique users:</strong> Musicians and music enthusiasts from around the world have used LyricJam for creative exploration and songwriting
                                </Typography>
                                <Typography variant="body2" sx={{ marginBottom: '10px' }}>
                                    <strong>Global reach:</strong> Users span multiple countries and continents, demonstrating the universal appeal of AI-assisted creativity
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Diverse sessions:</strong> From quick creative experiments to extended jam sessions, users have found multiple ways to integrate the system into their workflow
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Card sx={{ height: '100%', backgroundColor: '#f8f9fa' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#388e3c' }}>
                                    Creative Applications
                                </Typography>
                                <Typography variant="body2" sx={{ marginBottom: '10px' }}>
                                    <strong>Genre flexibility:</strong> While trained on rock music, users have successfully applied LyricJam across multiple genres including jazz, electronic, folk, and experimental music
                                </Typography>
                                <Typography variant="body2" sx={{ marginBottom: '10px' }}>
                                    <strong>Professional use:</strong> Musicians have integrated the system into live performances and songwriting processes as a creative catalyst
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Creative experimentation:</strong> Artists report using the system to explore new musical territories and overcome creative blocks
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            {/* Media Coverage Section */}
            <Box sx={{ marginBottom: '40px' }}>
                <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
                    Media Coverage
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6, marginBottom: '20px' }}>
                    LyricJam has received extensive coverage in technology and music media:
                </Typography>

                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                                    TechXplore
                                </Typography>
                                <Typography variant="body2" sx={{ marginBottom: '10px' }}>
                                    "LyricJam system generates lyrics for live instrumental music"
                                </Typography>
                                <Typography variant="body2" sx={{ marginBottom: '15px' }}>
                                    Featured article highlighting the real-time capabilities and potential applications of the system.
                                </Typography>
                                <Link
                                    href="https://techxplore.com/news/2021-06-lyricjam-lyrics-instrumental-music.html"
                                    target="_blank"
                                    rel="noopener"
                                    sx={{ fontSize: '0.9rem' }}
                                >
                                    Read Article
                                </Link>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                                    Mixmag
                                </Typography>
                                <Typography variant="body2" sx={{ marginBottom: '10px' }}>
                                    "This new AI tech can write lyrics to your instrumental tracks"
                                </Typography>
                                <Typography variant="body2" sx={{ marginBottom: '15px' }}>
                                    Coverage by the leading electronic music publication, exploring applications for electronic music producers and DJs.
                                </Typography>
                                <Link
                                    href="https://mixmag.net/read/ai-instrumental-lyrics-new-technology-track-ai-tech"
                                    target="_blank"
                                    rel="noopener"
                                    sx={{ fontSize: '0.9rem' }}
                                >
                                    Read Article
                                </Link>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                                    University of Waterloo News
                                </Typography>
                                <Typography variant="body2" sx={{ marginBottom: '10px' }}>
                                    "Researchers develop real-time lyric generation technology"
                                </Typography>
                                <Typography variant="body2" sx={{ marginBottom: '15px' }}>
                                    Official university coverage of the research breakthrough and its implications for AI-assisted creativity.
                                </Typography>
                                <Link
                                    href="https://uwaterloo.ca/news/media/researchers-develop-real-time-lyric-generation-technology"
                                    target="_blank"
                                    rel="noopener"
                                    sx={{ fontSize: '0.9rem' }}
                                >
                                    Read Article
                                </Link>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                                    Canadian Musician Magazine
                                </Typography>
                                <Typography variant="body2" sx={{ marginBottom: '10px' }}>
                                    Featured in industry publication covering music technology innovations
                                </Typography>
                                <Typography variant="body2" sx={{ marginBottom: '15px' }}>
                                    In-depth coverage for the professional music community about AI applications in creative processes.
                                </Typography>
                                <Link
                                    href="https://www.worldradiohistory.com/Archive-All-Music/CM-Canadian-Musician/20s/Canadian-Musician-2021-09-10.pdf"
                                    target="_blank"
                                    rel="noopener"
                                    sx={{ fontSize: '0.9rem' }}
                                >
                                    View Magazine
                                </Link>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            {/* Paper Section */}
            <Box sx={{ marginBottom: '40px' }}>
                <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
                    Research Paper
                </Typography>
                <Typography variant="body1" sx={{ marginBottom: '20px' }}>
                    Read the full research paper:
                </Typography>
                <Box sx={{
                    width: '100%',
                    height: '800px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    overflow: 'hidden'
                }}>
                    <iframe
                        src="/pdf/LyricJam-paper.pdf"
                        style={{
                            width: '100%',
                            height: '100%',
                            border: 'none'
                        }}
                        title="LyricJam ICCC 2021 Paper"
                    />
                </Box>
            </Box>

            {/* Links Section */}
            <Box sx={{ marginBottom: '40px' }}>
                <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
                    Resources
                </Typography>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Link
                            href="https://lyricjam.ai"
                            target="_blank"
                            rel="noopener"
                            sx={{ display: 'block', padding: '10px', textDecoration: 'none' }}
                        >
                            <Card sx={{ textAlign: 'center', '&:hover': { backgroundColor: '#f5f5f5' } }}>
                                <CardContent>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                        Web Application
                                    </Typography>
                                    <Typography variant="body2">
                                        Try LyricJam online
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Link>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Link
                            href="/pdf/LyricJam-slides.pdf"
                            target="_blank"
                            rel="noopener"
                            sx={{ display: 'block', padding: '10px', textDecoration: 'none' }}
                        >
                            <Card sx={{ textAlign: 'center', '&:hover': { backgroundColor: '#f5f5f5' } }}>
                                <CardContent>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                        Download Slides
                                    </Typography>
                                    <Typography variant="body2">
                                        Presentation from ICCC 2021
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Link>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Link
                            href="/pdf/LyricJam-paper.pdf"
                            target="_blank"
                            rel="noopener"
                            sx={{ display: 'block', padding: '10px', textDecoration: 'none' }}
                        >
                            <Card sx={{ textAlign: 'center', '&:hover': { backgroundColor: '#f5f5f5' } }}>
                                <CardContent>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                        Download Paper
                                    </Typography>
                                    <Typography variant="body2">
                                        PDF version of the research paper
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Link>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Link
                            href="https://arxiv.org/pdf/2106.01960.pdf"
                            target="_blank"
                            rel="noopener"
                            sx={{ display: 'block', padding: '10px', textDecoration: 'none' }}
                        >
                            <Card sx={{ textAlign: 'center', '&:hover': { backgroundColor: '#f5f5f5' } }}>
                                <CardContent>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                        ArXiv Paper
                                    </Typography>
                                    <Typography variant="body2">
                                        Pre-print version
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Link>
                    </Grid>
                </Grid>
            </Box>

            {/* Citation Section */}
            <Box sx={{ marginBottom: '40px' }}>
                <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
                    Citation
                </Typography>
                <Box sx={{
                    backgroundColor: '#f5f5f5',
                    padding: '20px',
                    borderRadius: '4px',
                    fontFamily: 'monospace',
                    fontSize: '0.9rem'
                }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        @inproceedings{'{'}vechtomova2021lyricjam,<br />
                        &nbsp;&nbsp;title={'{'}LyricJam: A system for generating lyrics for live instrumental music{'}'},<br />
                        &nbsp;&nbsp;author={'{'}Vechtomova, Olga and Sahu, Gaurav and Kumar, Dhruv{'}'},<br />
                        &nbsp;&nbsp;booktitle={'{'}Proceedings of the 12th International Conference on Computational Creativity{'}'},<br />
                        &nbsp;&nbsp;pages={'{'}122--130{'}'},<br />
                        &nbsp;&nbsp;year={'{'}2021{'}'},<br />
                        &nbsp;&nbsp;isbn={'{'}978-989-54160-3-5{'}'}<br />
                        {'}'}
                    </Typography>
                </Box>
            </Box>
        </Container>
    );
}

export default LyricJam;